import { useUser } from '@folklore/auth';
import Cookies from 'js-cookie';
import isArray from 'lodash-es/isArray';
import isEmpty from 'lodash-es/isEmpty';
import queryString from 'query-string';
import { use, useState, createContext, ReactNode } from 'react';
import { useSearch } from 'wouter';

export const CONSENT_GIVEN_COOKIE_NAME = 'consent_given';
export const CONSENT_SETTINGS_COOKIE_NAME = 'consent_settings';
export const CONSENTS_COOKIE_NAME = 'consents';

export const allCookieSettings = [
    {
        id: 'necessary',
        label: 'Fonctionnel',
        description: 'Cookies pour le bon fonctionnement du site.',
        consents: ['functionality_storage', 'security_storage', 'personalization_storage'],
    },
    {
        id: 'stats',
        label: 'Statistiques',
        description: 'Cookies pour mesurer le comportement des utilisateurs.',
        consents: ['analytics_storage'],
    },
    {
        id: 'ads',
        label: 'Publicitaire',
        description: 'Cookies pour l’affichage d’annonces personnalisées.',
        consents: ['ad_storage', 'ad_user_data', 'ad_personalization'],
    },
];

type ConsentStatus = 'granted' | 'denied';

type ConsentSettingId = (typeof allCookieSettings)[number]['id'];

type ConsentFeature =
    | 'ad_storage'
    | 'ad_user_data'
    | 'ad_personalization'
    | 'personalization_storage'
    | 'analytics_storage'
    | 'functionality_storage'
    | 'security_storage';

type ConsentState = Record<ConsentFeature, ConsentStatus>;

interface ConsentContextValue {
    cookiesSettings: ConsentSettingId[];
    consentGiven: boolean | null;
    consent: ConsentState;
    update: (consentGiven: boolean, settings?: ConsentSettingId[] | null) => void;
}

function getConsentsFromSettings(settings: ConsentSettingId[] | null): string[] {
    return (settings || []).reduce<string[]>((acc, settingId) => {
        const { consents } = allCookieSettings.find(({ id }) => id === settingId) || {};
        return [...acc, ...(consents || [])];
    }, []);
}

function getConsentState(
    consentGiven: boolean | null,
    settings: ConsentSettingId[] | null,
): ConsentState {
    const consents = getConsentsFromSettings(settings);
    return {
        ad_storage: consentGiven && consents.indexOf('ad_storage') !== -1 ? 'granted' : 'denied',
        ad_user_data:
            consentGiven && consents.indexOf('ad_user_data') !== -1 ? 'granted' : 'denied',
        ad_personalization:
            consentGiven && consents.indexOf('ad_personalization') !== -1 ? 'granted' : 'denied',
        personalization_storage:
            consentGiven && consents.indexOf('personalization_storage') !== -1
                ? 'granted'
                : 'denied',
        analytics_storage: consents.indexOf('analytics_storage') !== -1 ? 'granted' : 'denied',
        functionality_storage:
            consents.indexOf('functionality_storage') !== -1 ? 'granted' : 'denied',
        security_storage: consents.indexOf('security_storage') !== -1 ? 'granted' : 'denied',
    };
}

export function getConsentFromCookie() {
    const cookieConsentGiven = Cookies.get(CONSENT_GIVEN_COOKIE_NAME);
    return typeof cookieConsentGiven !== 'undefined' && !isEmpty(cookieConsentGiven)
        ? cookieConsentGiven === 'true'
        : null;
}

export function getCookiesSettingsFromCookie() {
    const cookieSettingsValue = Cookies.get(CONSENT_SETTINGS_COOKIE_NAME);
    return typeof cookieSettingsValue !== 'undefined' && !isEmpty(cookieSettingsValue)
        ? cookieSettingsValue.split(',').filter((it) => !isEmpty(it))
        : null;
}

const cookieConsentGiven = getConsentFromCookie();
const cookieSettingsFromCookie = getCookiesSettingsFromCookie();

const defaultConsent: ConsentState = getConsentState(cookieConsentGiven, cookieSettingsFromCookie);

export const ConsentContext = createContext<ConsentContextValue>({
    cookiesSettings: allCookieSettings.map(({ id }) => id),
    consentGiven: cookieConsentGiven === true,
    consent: defaultConsent,
    update: () => {},
});

export function useConsentContext(): ConsentContextValue {
    const context = use(ConsentContext);
    return context;
}

export function useUpdateConsent() {
    const { update } = useConsentContext();
    return update;
}

export function useConsentGiven() {
    const { consentGiven } = useConsentContext();
    return consentGiven;
}

export function useCookiesSettings() {
    const { cookiesSettings } = useConsentContext();
    return cookiesSettings;
}

export function useConsent(features: ConsentFeature | ConsentFeature[]) {
    const { consent } = useConsentContext();
    const hasConsent = (isArray(features) ? features : [features]).reduce(
        (all, feature) => all && consent[feature] === 'granted',
        true,
    );
    return hasConsent;
}

export function useConsentNeeded() {
    const consentGiven = useConsentGiven();
    const search = useSearch();
    const user = useUser();
    const { askConsent = false } = queryString.parse(search);
    return (user === null && consentGiven === null) || askConsent === 'true';
}

interface ConsentContextProviderProps {
    children?: ReactNode;
    expiration?: number;
}

export function ConsentContextProvider({
    children = null,
    expiration = 7,
}: ConsentContextProviderProps) {
    const user = useUser() || null;
    const [cookiesSettings, setCookiesSettings] = useState<ConsentSettingId[]>(
        cookieSettingsFromCookie || allCookieSettings.map(({ id }) => id),
    );
    const [consentGiven, setConsentGiven] = useState<boolean | null>(
        user !== null ? true : cookieConsentGiven,
    );
    const [consent, setConsent] = useState<ConsentState>(() =>
        getConsentState(consentGiven, cookiesSettings),
    );

    const update = (newConsentGiven: boolean, settings: ConsentSettingId[] | null = null) => {
        const newSettings = settings || allCookieSettings.map(({ id }) => id);
        const newConsents = getConsentsFromSettings(newSettings);
        const newConsent: ConsentState = getConsentState(newConsentGiven, newSettings);

        Cookies.set(CONSENT_GIVEN_COOKIE_NAME, newConsentGiven ? 'true' : 'false', {
            expires: expiration,
            domain: location.hostname.replace(/^(.*?)urbania\.(.*)$/, '.urbania.$2'),
        });
        Cookies.set(CONSENT_SETTINGS_COOKIE_NAME, newSettings.join(','), {
            expires: expiration,
            domain: location.hostname.replace(/^(.*?)urbania\.(.*)$/, '.urbania.$2'),
        });
        Cookies.set(CONSENTS_COOKIE_NAME, newConsents.join(','), {
            expires: expiration,
            domain: location.hostname.replace(/^(.*?)urbania\.(.*)$/, '.urbania.$2'),
        });
        if (typeof window.updateConsent !== 'undefined') {
            window.updateConsent(newConsent);
        }
        if (typeof window.dataLayer !== 'undefined') {
            window.dataLayer.push({
                event: 'consent_update',
                consentGiven: newConsentGiven,
                consents: newConsents,
                settings: newSettings,
            });
            if (newConsentGiven) {
                window.dataLayer.push({
                    event: 'consent_given',
                });
            }
        }

        setConsent({
            ...consent,
            ...newConsent,
        });
        setConsentGiven(newConsentGiven);
        setCookiesSettings(newSettings);
    };

    if (user !== null && consentGiven === null) {
        update(true);
    }

    const value = {
        cookiesSettings,
        consentGiven,
        update,
        consent,
    };

    return <ConsentContext value={value}>{children}</ConsentContext>;
}
